import React, { useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import { gql } from "generated-graphql";
import { format, formatDistanceToNow } from "date-fns";
import { CommentThreadFragment } from "generated-graphql/graphql";
import { getUserColor } from "encamp-shared/src/utils/colors";

gql(`
  fragment CommentThread on Comment {
    id
    reportId
    userId
    workflowId
    activityId
    comment
    isSystemMessage
    createdAt
    user {
      id
      email
      person {
        first
        last
      }
    }
  }
`);

interface CommentThreadProps {
  comments: CommentThreadFragment[];
  currentUserId?: string;
  onDeleteComment?: (commentId: string) => Promise<void>;
  deleteLoading?: boolean;
  emptyStateMessage?: string;
  showDeleteButton?: boolean;
}

export function CommentThread({
  comments,
  currentUserId,
  onDeleteComment,
  deleteLoading = false,
  emptyStateMessage = "No comments yet.",
  showDeleteButton = true,
}: CommentThreadProps) {
  // sort by creation date
  const sortedComments = useMemo(
    () =>
      [...comments].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    [comments]
  );

  const handleDeleteComment = async (commentId: string) => {
    if (onDeleteComment) {
      await onDeleteComment(commentId);
    }
  };

  if (sortedComments.length === 0) {
    // If no empty state message is provided, don't render anything
    if (!emptyStateMessage) {
      return null;
    }

    return (
      <Box
        sx={{
          p: 2,
          bgcolor: "grey.50",
          borderRadius: 1,
          textAlign: "center",
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          {emptyStateMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      {sortedComments.map((comment, index) => {
        const userColor = getUserColor(comment.user?.email ?? "");
        const canDelete =
          showDeleteButton &&
          currentUserId &&
          comment?.user?.id === currentUserId &&
          !comment.isSystemMessage &&
          onDeleteComment;

        return (
          <React.Fragment key={comment.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                "&:hover .delete-button": {
                  opacity: 1,
                },
              }}
            >
              {/* Quotation-style border */}
              <Box
                sx={{
                  width: 3,
                  minHeight: 40,
                  backgroundColor: userColor,
                  borderRadius: "1.5px",
                  flexShrink: 0,
                  alignSelf: "stretch",
                }}
              />

              {/* Delete button positioned just left of comment content */}
              {canDelete && (
                <IconButton
                  className="delete-button"
                  size="small"
                  onClick={() => handleDeleteComment(comment.id)}
                  disabled={deleteLoading}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.2s",
                    width: 16,
                    height: 16,
                    mt: 0.25,
                    "&:hover": {
                      opacity: 1,
                      backgroundColor: "rgba(211, 47, 47, 0.04)",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              )}

              {/* Comment content */}
              <Box
                sx={{
                  flex: 1,
                  pl: canDelete ? 0 : 1.5,
                }}
              >
                <Typography variant="body2">
                  <Typography component="span" fontWeight="bold">
                    {getCommentAuthor(comment)}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {!comment.isSystemMessage
                      ? ` ${formatDistanceToNow(
                          new Date(comment.createdAt)
                        )} ago`
                      : ` ${format(
                          new Date(comment.createdAt),
                          "M/d/yyyy 'at' h:mmaaa"
                        )}`}
                  </Typography>
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    comment.isSystemMessage ? "text.secondary" : "text.primary"
                  }
                  fontStyle={comment.isSystemMessage ? "italic" : "normal"}
                  sx={{ whiteSpace: "pre-wrap", mb: 0.5 }}
                >
                  {comment.comment}
                </Typography>
              </Box>
            </Box>
            {index < sortedComments.length - 1 && <Divider sx={{ my: 0.5 }} />}
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

function getCommentAuthor(comment: CommentThreadFragment) {
  return comment.user?.email ?? "Someone";
}
